// petconnect-v2/frontend/src/components/features/Search/SearchPage.js
// MERGED:
//   - Result cards are clickable → navigate to /post/:id
//   - owner_message field displayed
//   - Proper image aspect-ratio (object-contain)

import React, { useState } from 'react';
import { Search, MapPin, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../../hooks/useAPI';
import MapViewComponent from '../MapView/MapViewComponent';
import DualViewComponent from '../MapView/DualViewComponent';
import SearchFormComponent from './SearchFormComponent';
import NoResultsModal from '../../common/NoResultsModal';

// ── Safe image ────────────────────────────────────────────────────────────────
const SafeImage = ({ post, className = '' }) => {
  const [errored, setErrored] = useState(false);
  const src =
    Array.isArray(post.media_urls) && post.media_urls.length > 0
      ? post.media_urls[0]
      : typeof post.media_url === 'string' && post.media_url
      ? post.media_url
      : null;

  if (!src || errored) {
    return <div className={`flex items-center justify-center bg-gray-100 text-4xl ${className}`}>🐾</div>;
  }
  return (
    <img
      src={src}
      alt={post.pet_name || 'Pet photo'}
      className={className}
      onError={() => setErrored(true)}
    />
  );
};

// Normalise so media_urls is always an array
const normalisePost = (post) => {
  if (Array.isArray(post.media_urls)) return post;
  if (post.media_url) return { ...post, media_urls: [post.media_url] };
  return { ...post, media_urls: [] };
};

const SearchPage = ({ user }) => {
  const { apiCall } = useAPI();
  const navigate    = useNavigate();

  const [searchResults, setSearchResults]       = useState([]);
  const [searching, setSearching]               = useState(false);
  const [viewMode, setViewMode]                 = useState('list');
  const [hasSearched, setHasSearched]           = useState(false);
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState(null);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const handleSearch = async (searchParams) => {
    setSearching(true);
    setHasSearched(true);
    setLastSearchParams(searchParams);
    setViewMode('list');

    try {
      const searchData = {
        pet_name:    searchParams.pet_name || '',
        animal_type: searchParams.animal_type,
        breed:       searchParams.animal_type === 'dog' ? searchParams.breed : null,
        sex:         searchParams.sex !== 'all' ? searchParams.sex : null,
        description: searchParams.description || '',
        location:    searchParams.location,
        latitude:    searchParams.latitude,
        longitude:   searchParams.longitude,
        radius_km:   searchParams.radius_km,
        category:    searchParams.category !== 'all' ? searchParams.category : null,
      };

      const data = await apiCall('/search', {
        method: 'POST',
        body: JSON.stringify(searchData),
      });

      const results = (data.posts || []).map(normalisePost);
      setSearchResults(results);
      if (results.length === 0) setShowNoResultsModal(true);
    } catch (err) {
      console.error('Search error:', err);
      alert(`Search failed: ${err.message}`);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const geoResults = searchResults.filter(p => p.latitude != null && p.longitude != null);

  const VIEW_TABS = [
    { mode: 'list', label: '📋 List View',  disabled: false },
    { mode: 'map',  label: '🗺️ Map View',   disabled: geoResults.length === 0 },
    { mode: 'dual', label: '⚡ Dual View',  disabled: geoResults.length === 0 },
  ];

  return (
    <div className="max-w-6xl mx-auto pb-8">

      {/* Search Form */}
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-6">
          <Search className="text-blue-500" size={28} />
          Search Posts
        </h2>
        <SearchFormComponent onSearch={handleSearch} isLoading={searching} showCategoryFilter={true} />
      </div>

      {/* Results */}
      {searchResults.length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">
              Search Results ({searchResults.length} found)
            </h3>
            {geoResults.length > 0 && viewMode !== 'list' && (
              <p className="text-xs text-gray-500">{geoResults.length} post{geoResults.length !== 1 ? 's' : ''} with location</p>
            )}
          </div>

          {/* View toggles */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {VIEW_TABS.map(({ mode, label, disabled }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                disabled={disabled}
                title={disabled ? 'No posts with location data in results' : ''}
                className={`px-4 py-2 rounded-lg font-semibold transition text-sm ${
                  viewMode === mode
                    ? 'bg-blue-500 text-white shadow-md'
                    : disabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <div className="space-y-4">
              {searchResults.map((post) => {
                const mediaUrls = post.media_urls;
                return (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/post/${post.id}`)}
                    className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-purple-300 transition cursor-pointer group"
                  >
                    {/* Image */}
                    {mediaUrls.length > 0 && (
                      <div className="relative w-full bg-gray-50" style={{ paddingTop: '50%' }}>
                        {mediaUrls.length === 1 ? (
                          <SafeImage
                            post={post}
                            className="absolute inset-0 w-full h-full object-contain group-hover:opacity-95 transition"
                          />
                        ) : (
                          <div className="absolute inset-0 grid grid-cols-3 gap-0.5">
                            {mediaUrls.slice(0, 3).map((url, idx) => (
                              <div key={idx} className="relative bg-gray-100 overflow-hidden">
                                <SafeImage
                                  post={{ ...post, media_urls: [url] }}
                                  className="absolute inset-0 w-full h-full object-contain"
                                />
                                {idx === 2 && mediaUrls.length > 3 && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-xl">
                                    +{mediaUrls.length - 3}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition">
                          <ExternalLink size={12} />
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                      {/* Author + category */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 bg-purple-400 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {post.author?.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-bold text-gray-800 text-sm">{post.author?.username}</p>
                            <p className="text-xs text-gray-500">📅 {formatDateTime(post.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                            post.category === 'found'    ? 'bg-green-500' :
                            post.category === 'missing'  ? 'bg-red-500' :
                            post.category === 'adoption' ? 'bg-pink-500' : 'bg-blue-500'
                          }`}>
                            {post.category.toUpperCase()}
                          </span>
                          {post.status === 'resolved' && (
                            <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">✓ Reunited</span>
                          )}
                        </div>
                      </div>

                      {/* Pet info */}
                      {post.pet_name && (
                        <div className="bg-blue-50 border-l-4 border-blue-400 px-3 py-2 rounded mb-2">
                          <p className="text-sm font-semibold text-blue-900">
                            🐾 {post.pet_name}
                            {post.pet_type && <span className="font-normal"> ({post.pet_type})</span>}
                            {post.sex && post.sex !== 'unknown' && <span className="font-normal"> · {post.sex}</span>}
                          </p>
                          {post.missing_since && (
                            <p className="text-xs text-blue-700 mt-0.5">
                              {post.category === 'missing' ? 'Missing since' : 'Found on'}:{' '}
                              {new Date(post.missing_since).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}

                      <p className="text-gray-700 text-sm mb-3 line-clamp-3">{post.content}</p>

                      {/* Owner message */}
                      {post.owner_message && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                          <p className="text-xs font-semibold text-amber-800 mb-0.5">💬 Message from Owner:</p>
                          <p className="text-sm text-amber-900 line-clamp-2">{post.owner_message}</p>
                        </div>
                      )}

                      {post.location_name && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                          <MapPin size={13} />
                          <span>{post.location_name}</span>
                        </div>
                      )}

                      {post.contact_info && (
                        <div className="bg-green-50 border border-green-200 px-3 py-2 rounded mb-2">
                          <p className="text-sm text-green-900 font-semibold">📞 {post.contact_info}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                        <div className="flex gap-4">
                          <span>❤️ {post.likes_count}</span>
                          <span>💬 {post.comments_count}</span>
                        </div>
                        <span className="text-purple-500 font-semibold opacity-0 group-hover:opacity-100 transition">
                          View full post →
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {viewMode === 'map'  && <MapViewComponent  posts={geoResults} />}
          {viewMode === 'dual' && <DualViewComponent posts={geoResults} />}
        </div>
      )}

      {/* Pre-search placeholder */}
      {!hasSearched && !searching && (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <Search size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 text-lg">Fill in search criteria and click "Search Lost & Found Pets"</p>
        </div>
      )}

      <NoResultsModal
        isOpen={showNoResultsModal}
        onClose={() => setShowNoResultsModal(false)}
        searchParams={lastSearchParams}
      />
    </div>
  );
};

export default SearchPage;